/** The money as hg-billing's staff endpoints give it (`../hg-billing/openapi.json`). */
import type { components } from "@/shared/api/billing.schema";

type Schemas = components["schemas"];

export type WalletRow = Schemas["WalletRowResponse"];
export type ProviderWallet = Schemas["ProviderWalletResponse"];
export type WalletEntry = Schemas["ProviderWalletEntryResponse"];
export type WalletPart = Schemas["WalletPartResponse"];
export type WalletTotals = Schemas["WalletTotalsResponse"];
export type WalletCounts = Schemas["WalletCountsResponse"];
export type Person = Schemas["PersonResponse"];
export type Reference = Schemas["ReferenceResponse"];
export type Topup = Schemas["TopupResponse"];
export type TopupCall = Schemas["ProviderCallResponse"];
export type BankCall = Schemas["BankCallResponse"];
export type CardSide = Schemas["CardSideResponse"];
export type CardReview = Schemas["CardReviewResponse"];
export type ReviewReason = Schemas["ReviewReason"];
export type CardPhase = Schemas["Phase"];
export type CardFailure = Schemas["CardFailure"];
export type TopupCounts = Schemas["TopupCountsResponse"];
export type TopupState = Schemas["TopupState"];
export type TopupProvider = Schemas["Provider"];
export type Adjustment = Schemas["AdjustmentResponse"];
export type LedgerAccount = Schemas["LedgerAccountResponse"];
export type LedgerEntry = Schemas["LedgerEntryResponse"];
export type LedgerHealth = Schemas["LedgerHealthResponse"];
export type AccountRef = Schemas["AccountResponse"];
export type AccountClass = Schemas["AccountClass"];
export type TopupSetting = Schemas["TopupSettingResponse"];
export type Payout = Schemas["PayoutResponse"];
export type PayoutStatus = Schemas["PayoutStatus"];
export type PayoutCounts = Schemas["PayoutCountsResponse"];
export type PayoutAction = Schemas["PayoutAction"];
export type MarkPaidInput = Schemas["MarkPayoutPaidRequest"];
export type WalletSummary = Schemas["WalletSummaryResponse"];
export type SavedCard = Schemas["StaffCardResponse"];
export type BankCallRow = Schemas["BankCallRowResponse"];
export type BankCallOutcome = Schemas["BankCallOutcome"];
export type MoneyReport = Schemas["MoneyReportResponse"];
export type ReportPeriod = Schemas["ReportPeriodResponse"];
export type ReportMoney = Schemas["ReportMoneyResponse"];
export type ReportGroup = Schemas["Group"];
export type TopupSettingInput = Schemas["SetTopupSettingRequest"];
export type AcceptMinimum = Schemas["AcceptMinimumResponse"];
export type AcceptMinimumInput = Schemas["SetAcceptMinimumRequest"];

/**
 * The bank's channel. Its rules are a top-up channel's, and its switch is
 * more: it is the one switch for every use of a card (saving one, topping up
 * from one), in both apps. v1's `card_payments_enabled`.
 */
export const CARD_CHANNEL = "hamkorbank" satisfies TopupProvider;

/** The channels whose rules are set here: the payment systems, then the cards. */
export const SETTABLE_CHANNELS = [
  "payme",
  "click",
  "paynet",
  CARD_CHANNEL,
] as const satisfies readonly TopupProvider[];
export type AdjustmentInput = Schemas["CreateAdjustmentRequest"];
export type AdjustmentStatus = Schemas["AdjustmentStatus"];
export type Direction = Schemas["Direction"];
export type ReasonCode = Schemas["ReasonCode"];

export const ADJUSTMENT_STATUSES = [
  "pending",
  "posted",
  "rejected",
  "withdrawn",
] as const satisfies readonly AdjustmentStatus[];

/** Where a top-up stands, in the order the tabs show them. */
export const TOPUP_STATES = [
  "in_suspense",
  "performed",
  "created",
  "failed",
  "cancelled",
  "reversed",
] as const satisfies readonly TopupState[];

/** Why a card top-up is marked for staff: each says what staff should do. */
export const REVIEW_REASONS = [
  "money_returned_after_credit",
  "charged_after_failure",
  "unfinished_after_cutoff",
] as const satisfies readonly ReviewReason[];

/** Where a card payment stands at the bank. */
export const CARD_PHASES = [
  "creating",
  "awaiting_code",
  "confirming",
  "cancelling",
  "done",
] as const satisfies readonly CardPhase[];

/** Why a card top-up failed, as hg-billing says it (never the bank's own words). */
export const CARD_FAILURES = [
  "bank_unavailable",
  "card_insufficient_funds",
  "card_declined",
  "card_code_wrong",
  "card_code_expired",
  "card_not_found",
  "card_expired",
  "card_blocked",
  "card_not_supported",
  "card_details_wrong",
  "card_limit_exceeded",
  "card_try_later",
  "card_already_added",
  "topup_expired",
] as const satisfies readonly CardFailure[];

/** How a call to the bank ended. */
export const BANK_CALL_OUTCOMES = [
  "answered",
  "refused",
  "no_answer",
] as const satisfies readonly BankCall["outcome"][];

/** The bank's methods hg-billing calls, and `token` for the bank's token asked for on the way. */
export const BANK_METHODS = [
  "pay.create",
  "pay.confirm",
  "pay.get",
  "pay.get.ext",
  "pay.cancel",
  "sms.resend",
  "card.create",
  "card.verify",
  "token",
] as const;

/** How the report's days are put together. */
export const REPORT_GROUPS = ["day", "week", "month"] as const satisfies readonly ReportGroup[];

/** How a payment system's call ended; any other word is shown as the service says it. */
export const CALL_RESULTS = [
  "applied",
  "repeated",
  "refused",
  "expired",
  "contested",
  "answered",
] as const satisfies readonly NonNullable<TopupCall["result"]>[];

/** The states `GET /v2/admin/wallets` narrows by. */
export const WALLET_STATES = ["in_debt", "below_minimum", "can_accept"] as const;
export type WalletState = (typeof WALLET_STATES)[number];

/** How the wallets list is ordered: the deepest in debt first, or the richest first. */
export const WALLET_SORTS = ["balance", "-balance"] as const;
export type WalletSort = (typeof WALLET_SORTS)[number];

/** The types of a wallet's lines the app has words for; any other is shown as the service names it. */
export const ENTRY_TYPES = [
  "visit_settlement",
  "visit_settlement_reversal",
  "topup",
  "topup_reversal",
  "topup_assignment",
  "adjustment",
  "referral_bonus",
  "payout",
  "payout_reserve",
  "payout_release",
  "course_bonus",
  "v1_opening",
  "v1_adjustment",
] as const;

/** The kinds of the platform's own accounts the app has words for; `clearing:<system>` is said by its system. */
export const ACCOUNT_KINDS = [
  "commission_revenue",
  "promo_expense",
  "topup_fee_revenue",
  "penalty_revenue",
  "goodwill_expense",
  "referral_expense",
  "suspense",
  "wallet",
  "wallet_reserved",
  "partner_wallet",
  "corrections",
  "bank",
] as const;

export const ACCOUNT_CLASSES = [
  "revenue",
  "expense",
  "asset",
  "liability",
  "balancing",
] as const satisfies readonly AccountClass[];
export type EntryType = (typeof ENTRY_TYPES)[number];

/** Why staff adjusted a wallet. */
export const ADJUSTMENT_REASONS = [
  "correction",
  "goodwill",
  "penalty",
] as const satisfies readonly ReasonCode[];

/** The payment systems a top-up comes through. */
export const TOPUP_CHANNELS = [
  "payme",
  "click",
  "paynet",
  "hamkorbank",
] as const satisfies readonly TopupProvider[];

/** Where a payout stands, in the order a payout lives through them. */
export const PAYOUT_STATUSES = [
  "requested",
  "approved",
  "processing",
  "paid",
  "rejected",
  "cancelled",
] as const satisfies readonly PayoutStatus[];

/** Whose wallet a payout is out of: a specialist's provider, or a referral partner (v1's history). */
export const PAYOUT_OWNERS = [
  "provider",
  "partner",
] as const satisfies readonly Schemas["OwnerType"][];
export type PayoutOwner = (typeof PAYOUT_OWNERS)[number];

/** Whose wallet a page shows: a provider's, or that of the provider a specialist works through. */
export interface WalletOwner {
  by: "provider" | "specialist";
  id: string;
}
